import React, { useEffect, useState } from "react";
import axios from "axios";
import authgear from "../authgear";

const base_url = process.env.NODE_ENV === "production" ? "https://hooks-28xd.onrender.com" : "http://localhost:4000";

const PLATFORM_LABEL = { macos: "macOS", windows: "Windows" };

const smallBtn = (color, disabled) => ({
    padding: "4px 10px",
    background: "transparent",
    color: disabled ? "#555" : color,
    border: `1px solid ${disabled ? "#2a2a4a" : color}`,
    borderRadius: "5px",
    cursor: disabled ? "not-allowed" : "pointer",
    fontSize: "12px",
    fontWeight: "bold",
});

const formatDate = (iso) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

// TrainPane beta signups, proxied through /trainpane. Renders nothing while the queue is
// empty, or when the backend has no TRAINPANE_ADMIN_TOKEN or TrainPane can't be reached.
const TrainpaneBetaBanner = ({ cardStyle }) => {
    const [signups, setSignups] = useState([]);
    const [expanded, setExpanded] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [confirmingClearAll, setConfirmingClearAll] = useState(false);
    const [error, setError] = useState(null);

    const authHeaders = async () => {
        await authgear.refreshAccessTokenIfNeeded();
        return { Authorization: `Bearer ${authgear.accessToken}` };
    };

    useEffect(() => {
        authHeaders()
            .then(headers => axios.get(`${base_url}/trainpane/beta-signups`, { headers }))
            .then(({ data }) => setSignups(data.signups || []))
            .catch(() => {});
    }, []);

    const clearOne = async (id) => {
        setBusyId(id);
        setError(null);
        try {
            await axios.delete(`${base_url}/trainpane/beta-signups/${id}`, { headers: await authHeaders() });
            setSignups(prev => prev.filter(s => s.id !== id));
        } catch (e) {
            if (e.response?.status === 404) setSignups(prev => prev.filter(s => s.id !== id));
            else setError(e.response?.data?.message || e.message);
        } finally {
            setBusyId(null);
        }
    };

    const setInvited = async (id, invited) => {
        setBusyId(id);
        setError(null);
        try {
            const { data } = await axios.patch(
                `${base_url}/trainpane/beta-signups/${id}`,
                { invited },
                { headers: await authHeaders() }
            );
            setSignups(prev => prev.map(s => (s.id === id ? { ...s, ...data.signup } : s)));
        } catch (e) {
            // A 404 here can also mean TrainPane doesn't have the PATCH route yet, so
            // don't drop the row the way clearOne does.
            setError(e.response?.data?.message || e.message);
        } finally {
            setBusyId(null);
        }
    };

    const clearAll = async () => {
        setBusyId("all");
        setError(null);
        try {
            await axios.delete(`${base_url}/trainpane/beta-signups`, { headers: await authHeaders() });
            setSignups([]);
        } catch (e) {
            setError(e.response?.data?.message || e.message);
        } finally {
            setBusyId(null);
            setConfirmingClearAll(false);
        }
    };

    if (signups.length === 0) return null;

    const busy = busyId !== null;
    const invited = signups.filter(s => s.invitedAt).length;
    const waiting = signups.length - invited;

    return (
        <div style={{ ...cardStyle, marginBottom: "24px", borderColor: "#4a3a1a" }}>
            <div
                onClick={() => setExpanded(e => !e)}
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", gap: "8px" }}
            >
                <span style={{ color: "#aaa", fontSize: "13px" }}>
                    <span style={{ color: "#555", marginRight: "8px" }}>{expanded ? "▾" : "▸"}</span>
                    TrainPane beta queue
                </span>
                <span style={{ color: "#fbbf24", fontWeight: "bold", fontSize: "15px" }}>
                    {waiting} waiting
                    {invited > 0 && <span style={{ color: "#22c55e", fontWeight: "normal", fontSize: "13px" }}> · {invited} invited</span>}
                </span>
            </div>

            {expanded && (
                <div style={{ marginTop: "14px", display: "flex", flexDirection: "column", gap: "8px" }}>
                    {signups.map(s => (
                        <div key={s.id} style={{ background: "#0d0d1a", borderRadius: "6px", padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
                            <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: "13px", color: "#ddd", wordBreak: "break-all" }}>
                                    {s.email}{s.name ? <span style={{ color: "#888" }}> ({s.name})</span> : null}
                                </div>
                                <div style={{ fontSize: "11px", color: "#555", marginTop: "2px" }}>
                                    {PLATFORM_LABEL[s.platform] || s.platform} · signed up {formatDate(s.createdAt)}
                                    {s.invitedAt ? <span style={{ color: "#22c55e" }}> · invited {formatDate(s.invitedAt)}</span> : null}
                                </div>
                                {s.equipment ? <div style={{ fontSize: "12px", color: "#888", marginTop: "4px" }}>{s.equipment}</div> : null}
                            </div>
                            <div style={{ display: "flex", gap: "6px" }}>
                                <button onClick={() => setInvited(s.id, !s.invitedAt)} disabled={busy} style={smallBtn(s.invitedAt ? "#888" : "#22c55e", busy)}>
                                    {busyId === s.id ? "Saving…" : s.invitedAt ? "Uninvite" : "Mark invited"}
                                </button>
                                <button onClick={() => clearOne(s.id)} disabled={busy} style={smallBtn("#f87171", busy)}>
                                    Clear
                                </button>
                            </div>
                        </div>
                    ))}

                    {error && <div style={{ fontSize: "12px", color: "#f88" }}>{error}</div>}

                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "4px" }}>
                        {confirmingClearAll ? (
                            <>
                                <span style={{ fontSize: "12px", color: "#aaa", alignSelf: "center" }}>
                                    Permanently delete all {signups.length}?
                                </span>
                                <button onClick={() => setConfirmingClearAll(false)} disabled={busy} style={smallBtn("#888", busy)}>Cancel</button>
                                <button onClick={clearAll} disabled={busy} style={smallBtn("#f87171", busy)}>
                                    {busyId === "all" ? "Clearing…" : "Delete all"}
                                </button>
                            </>
                        ) : (
                            <button onClick={() => setConfirmingClearAll(true)} disabled={busy} style={smallBtn("#f87171", busy)}>Clear all</button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TrainpaneBetaBanner;
